
import { defineComponent, reactive, ref, PropType, onMounted, onBeforeUnmount } from 'vue'
import { DialogAction } from '@/types/dialog'
import { DialogType, DialogButtonText, DialogMessageTemplate } from '@/enums/dialog'
import { ToastText } from '@/enums/toast'
import { DialogEvent } from '@/enums/eventType'
import { emitter } from '@/helpers/eventBusHelper'
import { IconType } from '@/enums/IconType'
import Accordion from '@/components/base/Accordion.vue'
import Button, { ButtonThemeStyle, ButtonSizeStyle } from '@/components/base/Button.vue'
import Check from '@/components/base/controls/Check.vue'
import store, { MutationName } from '@/store'
import _ from 'lodash'
import { RepairItemSection, RepairItemSectionItem } from '@/types/popup'
import FullscreenPopup from '@/components/composite/FullscreenPopup.vue'

export default defineComponent({
  components: {
    Accordion,
    Button,
    Check,
    FullscreenPopup,
  },
  emits: ['confirm', 'close'],
  props: {
    selectedItems: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    dialogType: {
      type: Number as PropType<DialogType>,
      default: DialogType.NORMAL,
    },
  },
  setup(props, { emit }) {
    const root = ref()
    const textarea = ref()
    const sections = reactive<RepairItemSection[]>([
      {
        id: 1,
        name: '자전거',
        items: [
          // selectedItems props로 check하기 때문에, 정렬되어 있어야 합니다.
          { checked: false, text: '거치대' },
          { checked: false, text: '기어' },
          { checked: false, text: '니플' },
          { checked: false, text: '라이트' },
          { checked: false, text: '바퀴' },
          { checked: false, text: '배터리 핀 빠짐' },
          { checked: false, text: '벨' },
          { checked: false, text: '브레이크' },
          { checked: false, text: '손잡이' },
          { checked: false, text: '안장' },
          { checked: false, text: '체인' },
          { checked: false, text: '페달' },
        ],
      },
      {
        id: 2,
        name: '스마트락',
        items: [
          { checked: false, text: '라이트 + 모터전원' },
          { checked: false, text: '라이트 전원' },
          { checked: false, text: '모터 전원' },
          { checked: false, text: '배터리 잔량오류' },
          { checked: false, text: '스마트락 충전오류' },
          { checked: false, text: '스마트락 파손' },
          { checked: false, text: '주기보고 안됨' },
          { checked: false, text: 'GPS 오류' },
        ],
      },
      {
        id: 3,
        name: '기타',
        items: [
          { checked: false, text: '사고기기' },
          { checked: false, text: '직접입력' },
        ],
      },
    ])

    const manualInputText = ref('')

    props.selectedItems.forEach((item: string) => {
      if (item.match(/^유저제보 고장부위 /)) {
        const texts = item
          .replace(/^유저제보 고장부위 /, '')
          .trim()
          .split(',')
          .sort()
        let cursor = 0
        sections[0].items.forEach((item: RepairItemSectionItem) => {
          if (item.text === texts[cursor]) {
            item.checked = true
          }

          while (item.text > texts[cursor]) {
            cursor++
          }
        })
      } else {
        const index = item.match(/^자전거 - /) ? 0 : item.match(/^스마트락 - /) ? 1 : item.match(/^기타 - /) ? 2 : -1

        if (index === -1) {
          return
        }

        const text = item.replace(/자전거 - |스마트락 - |기타 - /g, '')
        if (index === 2 && text !== '사고기기') {
          sections[index].items[1].checked = true
          manualInputText.value = text
        }

        sections[index].items.forEach((item: RepairItemSectionItem) => {
          if (item.text === text) {
            item.checked = true
          }
        })
      }
    })

    const onItemClick = ({ item }: any): boolean => {
      const willBeChecked = !item.checked

      if (!willBeChecked) {
        item.checked = false
        return false
      }

      item.checked = !item.checked
      return false
    }

    const onRepairItemAddApplyButtonClick = () => {
      const getSelectedRepairItems = () => {
        const ret = []
        for (let i = 0; i < sections.length; i++) {
          for (const item of sections[i].items) {
            if (item.checked) {
              ret.push(item)
            }
          }
        }
        return ret
      }

      const selectedRepairItems = getSelectedRepairItems()
      const selectedRepairItemCount = selectedRepairItems.length

      if (sections[2].items[1].checked) {
        if (manualInputText.value.trim().length === 0) {
          store.commit(MutationName.SET_TOAST, {
            message: ToastText.PLEASE_TYPE_MANUTAL_INPUT_TEXT,
            show: true,
          })
          return
        }
      } else if (selectedRepairItemCount === 0) {
        store.commit(MutationName.SET_TOAST, {
          message: ToastText.PLEASE_SELECT_ITEM_AT_LEAST_ONE,
          show: true,
        })
        return
      }

      switch (props.dialogType) {
        case DialogType.NORMAL:
          emitter.emit(DialogEvent.SHOW_VERTICAL, {
            message: DialogMessageTemplate.QUESTION_CREATE_REPAIR.replace('{{ count }}', `${selectedRepairItemCount}`),
            action1: { target: root.value, text: DialogButtonText.CANCEL },
            action2: { target: root.value, text: DialogButtonText.CONFIRM },
          })
          break
        case DialogType.CHANGE_TO_REPAIR_FIELD:
          emitter.emit(DialogEvent.SHOW_VERTICAL, {
            message: DialogMessageTemplate.QUESTION_CHANGE_REPAIR_FIELD.replace(
              '{{ count }}',
              `${selectedRepairItemCount}`
            ),
            action1: { target: root.value, text: DialogButtonText.CANCEL },
            action2: { target: root.value, text: DialogButtonText.CONFIRM },
          })
          break
        case DialogType.CHANGE_TO_REPAIR_WAREHOUSE:
          emitter.emit(DialogEvent.SHOW_VERTICAL, {
            message: DialogMessageTemplate.QUESTION_CHANGE_REPAIR_WAREHOUSE.replace(
              '{{ count }}',
              `${selectedRepairItemCount}`
            ),
            action1: { target: root.value, text: DialogButtonText.CANCEL },
            action2: { target: root.value, text: DialogButtonText.CONFIRM },
          })
          break
        case DialogType.RELOCATION_REPAIR_FIELD:
          emitter.emit(DialogEvent.SHOW_HORIZONTAL, {
            message: DialogMessageTemplate.QUESTION_CREATE_REPAIR_FIELD.replace(
              '{{ count }}',
              `${selectedRepairItemCount}`
            ),
            action1: { target: root.value, text: DialogButtonText.ADD_REPAIR_TYPE_FIELD },
            action2: { target: root.value, text: DialogButtonText.COMPLETE_RELOCATION_AND_REPAIR_FIELD },
          })
          break
        case DialogType.RELOCATION_REPAIR_WAREHOUSE:
          emitter.emit(DialogEvent.SHOW_HORIZONTAL, {
            message: DialogMessageTemplate.QUESTION_CREATE_REPAIR_WAREHOUSE.replace(
              '{{ count }}',
              `${selectedRepairItemCount}`
            ),
            action1: { target: root.value, text: DialogButtonText.ADD_REPAIR_TYPE_WAREHOUSE },
            action2: { target: root.value, text: DialogButtonText.COMPLETE_RELOCATION_AND_REPAIR_WAREHOUSE },
          })
          break
        case DialogType.BATTERY_REPAIR_FIELD:
          emitter.emit(DialogEvent.SHOW_HORIZONTAL, {
            message: DialogMessageTemplate.QUESTION_CREATE_REPAIR_FIELD.replace(
              '{{ count }}',
              `${selectedRepairItemCount}`
            ),
            action1: { target: root.value, text: DialogButtonText.ADD_REPAIR_TYPE_FIELD },
            action2: { target: root.value, text: DialogButtonText.COMPLETE_BATTERY_AND_CREATE_REPAIR_FIELD },
          })
          break
        case DialogType.BATTERY_REPAIR_WAREHOUSE:
          emitter.emit(DialogEvent.SHOW_HORIZONTAL, {
            message: DialogMessageTemplate.QUESTION_CREATE_REPAIR_WAREHOUSE.replace(
              '{{ count }}',
              `${selectedRepairItemCount}`
            ),
            action1: { target: root.value, text: DialogButtonText.ADD_REPAIR_TYPE_WAREHOUSE },
            action2: { target: root.value, text: DialogButtonText.COMPLETE_BATTERY_AND_CREATE_REPAIR_WAREHOUSE },
          })
          break
        default:
          break
      }
    }

    const onTextareaResize = () => {
      textarea.value.style.height = 'auto'
      textarea.value.style.height = `${textarea.value.scrollHeight}px`
    }

    const dialogClickHandler = (action?: DialogAction) => {
      if (!action || action.target !== root.value) {
        return
      }

      const selectedItems = sections.flatMap((section: RepairItemSection) =>
        section.items
          .filter((item: RepairItemSectionItem) => item.checked)
          .map((item: RepairItemSectionItem) => `${section.name} - ${item.text}`)
      )

      const lastItem = _.last(sections[sections.length - 1].items)

      if (lastItem && lastItem.checked) {
        selectedItems[selectedItems.length - 1] = manualInputText.value.trim()
      }

      if (action.text === DialogButtonText.CANCEL) {
        emitter.emit(DialogEvent.HIDE)
      } else {
        emitter.emit(DialogEvent.HIDE)
        emit('confirm', action, selectedItems)
      }
    }

    onMounted(() => {
      emitter.on(DialogEvent.CLICK, dialogClickHandler)
    })

    onBeforeUnmount(() => {
      emitter.off(DialogEvent.CLICK, dialogClickHandler)
    })

    return {
      root,
      textarea,
      sections,
      manualInputText,
      onItemClick,
      onRepairItemAddApplyButtonClick,
      onTextareaResize,
      IconType,
      ButtonThemeStyle,
      ButtonSizeStyle,
    }
  },
})
